import { collection, getDocs } from 'firebase/firestore';
import { CrmPartnerSchema, type CrmPartner } from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal } from '@/lib/errorReport';

/**
 * Parceiros de revenda (#743) para o seletor da troca de tipo comercial. O
 * super-admin lê `crm_partners` direto (rules); o cadastro é do CRM.
 * Doc ilegível é reportado e pulado — a lista não pode sumir inteira por um.
 *
 * Origem: #743 — commit 985c25dc7, 2026-09-25 (ativação pelo parceiro).
 *
 * Contrato: leitura única (`getDocs`, sem listener), ordenada por nome. Doc
 * inválido → `reportNonFatal('admin_crm_partner_unparseable')` e pula. Erro
 * de leitura rejeita a promise (o chamador trata).
 *
 * Usado por: `features/billing/ChangeCommercialModeDialog.tsx`,
 * `features/api-keys/ApiKeyEditorDialog.tsx` e `routes/ApiKeysPage.tsx`.
 *
 * Arquitetura: rule `match /crm_partners/{partnerId}`; quem escreve é o CRM
 * (`apps/crm-functions`), nunca o admin.
 *
 * Dados: `crm_partners/{partnerId}` — schema `CrmPartnerSchema` em
 * `@etiquetachef/types`; o id do doc é injetado como `id` DEPOIS do spread,
 * para vencer um `id` gravado no corpo.
 */
export async function listCrmPartners(): Promise<CrmPartner[]> {
  const snap = await getDocs(collection(firestore, 'crm_partners'));
  const out: CrmPartner[] = [];
  for (const d of snap.docs) {
    const parsed = CrmPartnerSchema.safeParse({ ...d.data(), id: d.id });
    if (parsed.success) out.push(parsed.data);
    else reportNonFatal('admin_crm_partner_unparseable', { partnerId: d.id });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}
